import { fetchPopularAirports } from 'actions/FlightActions'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { ChangeEventHandler, ComponentProps } from 'react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import OfferAirportOptionsList from './OfferAirportOptionsList'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import { useAssistantSurfaceDropdownBoundary } from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'

interface Props extends Pick<ComponentProps<typeof DropdownList>, 'boundaryRef' | 'open' | 'anchorRef' | 'triggerRef'> {
  onClose: () => void
  onSelectAirport: (airport: App.AirportLocation) => void
  selectedAirportCode?: string
}

function OfferAirportSelectDropdown(props: Props) {
  const {
    open,
    anchorRef,
    boundaryRef,
    triggerRef,
    onClose,
    onSelectAirport,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownBoundary = useAssistantSurfaceDropdownBoundary(boundaryRef)

  const dispatch = useAppDispatch()
  const isMobile = useScreenSizeOnly('mobile')
  const intl = useIntl()

  useEffect(() => {
    if (open && !isMobile) {
      inputRef.current?.focus()
    }
  }, [isMobile, open])

  useEffect(() => {
    dispatch(fetchPopularAirports())
  }, [dispatch])

  const [searchPhrase, setSearchPhrase] = useState('')

  const handleSearchPhraseInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchPhrase(event.target.value)
  }, [])

  const title = intl.formatMessage({
    id: 'c5bwlaN-Xi8I2hRvxfCb',
    defaultMessage: 'Where will you depart from?',
    description: 'Heading title for airport selection dropdown on the offer detail page',
  })

  const subtitle = intl.formatMessage({
    id: '4VAXWmkiHUWA5fdFghXO',
    defaultMessage: 'Select your preferred airport',
    description: 'Subtitle for airport selection dropdown on the offer detail page',
  })

  const applyButtonText = intl.formatMessage({
    id: 'VnPR5gGxqSF98V8makLE',
    defaultMessage: 'Apply',
    description: 'Action button label to apply airport selection on the offer detail page',
  })

  const searchPlaceholder = intl.formatMessage({
    id: 'mqMo_fmGhVuXciY5Cv0L',
    defaultMessage: 'Search by city or airport',
    description: 'Placeholder text for airport search input in the flights flow',
  })

  return <DropdownList
    size="M"
    open={open && !isMobile}
    anchorRef={anchorRef}
    autoHeight={dropdownBoundary.autoHeight}
    boundaryRef={dropdownBoundary.boundaryRef}
    triggerRef={triggerRef}
    title={title}
    subtitle={subtitle}
    onClose={onClose}
    primaryActionProps={{
      children: applyButtonText,
      onClick: onClose,
    }}
    portaled={dropdownBoundary.portaled}
    headerExtension={<TextInputOnly
      placeholder={searchPlaceholder}
      noValidationMessage
      noValidationSpacing
      onChange={handleSearchPhraseInputChange}
      endIcon={<LineSearchIcon />}
      ref={inputRef}
    />}
  >
    <OfferAirportOptionsList
      searchPhrase={searchPhrase}
      onSelectAirport={onSelectAirport}
    />
  </DropdownList>
}

export default memo(OfferAirportSelectDropdown)
